import { DropdownButtonType, FilteringList, ListItem, SingleSelectDropdown, TabStyles } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';

import { getAppStore } from '~src/app/AppStore';
import { SessionUserSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { CreateScheduleAction } from '~src/app/spaces/Explorer/CreateContent/CreateContentActionsList';
import { IScheduleInfo } from '~src/schedule/types/IScheduleInfo';

import { Messages } from '../../codegen/Localize';
import { ISchedule, ScheduleActions } from '../../schedule/types/ISchedule';
import { SliceableHelper } from '../../ts/helpers/SliceableHelper';
import { SchedulesService } from '../../ts/resources/Schedules';

const addScheduleStyle = style({
  color: `${TabStyles.Colors.F9}`,
  textDecoration: 'underline',
  cursor: 'pointer',
  paddingTop: `${TabStyles.Spacing.DefaultSpace}px`,
  paddingBottom: `${TabStyles.Spacing.DefaultSpace}px`,
  fontWeight: 'bold',
});

export const nonAdminMessage = style({
  marginTop: `${TabStyles.Spacing.DefaultSpace}px`,
  borderTop: `1px solid ${TabStyles.Colors.F8}`,
  padding: `${TabStyles.Spacing.MediumSpace}px 0`,
  color: `${TabStyles.Colors.Black}`,
});

const filteringListStyle: React.CSSProperties = {
  maxHeight: '200px',
  paddingLeft: '4px',
  paddingRight: '4px',
  paddingTop: '8px',
  overflowY: 'auto',
  overflowX: 'hidden',
  boxSizing: 'border-box',
};

export type FlowScheduleSelectorProps = {
  readonly scheduleId: string;
  readonly dispatch: React.Dispatch<FlowScheduleSelectorAction>;
  readonly label?: string;
  readonly labelId?: string;
  readonly forLinkedTasks?: boolean;
};

export type FlowScheduleSelectorAction = { type: 'setSelectedSchedule', value: FlowScheduleSelectorProps['scheduleId'] };

export function createListItem(item: ISchedule): ListItem {
  const scheduleText = `${item.name} \u2014 ${IScheduleInfo.getScheduleDescription(item)}`;
  return {
    content: scheduleText,
    tooltip: scheduleText,
    value: item.id
  };
}

export function FlowScheduleSelector(props: FlowScheduleSelectorProps) {
  const [listItems, setListItems] = React.useState<ListItem[]>([]);
  const [filteredList, setFilteredList] = React.useState<ListItem[]>([]);
  const [searchText, setSearchText] = React.useState<string>('');
  const [initialized, setInitialized] = React.useState(false);
  const [closeDropdown] = React.useState<{ done: () => void }>({ done: null });

  // Initial load of the content
  React.useEffect(() => {
    if (!initialized) {
      fetchList();
      setInitialized(true);
    }
  }, [initialized]);

  React.useEffect(() => {
    const filteredList = listItems.filter((item: ListItem) => {
      return item.content && (item.content as string).toLowerCase().indexOf(searchText.toLowerCase()) >= 0;
    });
    setFilteredList(filteredList);
  }, [listItems, searchText]);

  function fetchList() {
    const schedulesQuery = SchedulesService.all();
    const flowSchedules = schedulesQuery.addFilter(SchedulesService.filterByActionType(ScheduleActions.Flow));
    SliceableHelper.fetchAll(flowSchedules).then(schedules => {
      const listItems: ListItem[] = [];
      schedules.items.forEach((item: ISchedule) => {
        if (item.enabledForLinkedTasks) {
          listItems.push(createListItem(item));
        }
      });
      setListItems(listItems);
    });
  }

  function onOpen(done: () => void) {
    closeDropdown.done = done;
    // On dropdown open we query for the latest schedules
    fetchList();
  }

  function onClose() {
    if (closeDropdown.done !== null) {
      closeDropdown.done();
      closeDropdown.done = null;
    }
  }

  function onSelect(value: string) {
    if (value === 'addSchedule') {
      const reduxAction = CreateScheduleAction.createReduxAction('Flow', true);
      getAppStore().dispatch(reduxAction);
    } else {
      props.dispatch({ type: 'setSelectedSchedule', value });
    }
    onClose();
  }

  function getScheduleTextFromId(id: string): string {
    return id && listItems.length !== 0 ?
      listItems.find(item => item.value === id).content as string
      : Messages.selectASchedule();
  }

  function itemsForLinkedTasks() {
    const items: ListItem[] = [{
      content: <div className={nonAdminMessage}>{Messages.linkedTasksEnabledSchedulePrompt()}</div>,
      disabled: true,
      value: 'disabled',
    }];

    // If the user is a server admin, give option to add schedule
    const appState = getAppStore().getState();
    if (SessionUserSelectors.isSessionServerAdmin(appState)) {
      items.unshift({
        content: <div className={addScheduleStyle}>{Messages.linkedTasksAddSchedule()}</div>,
        value: 'addSchedule',
      });
    }
    return filteredList.concat(items);
  }

  const listProps = {
    items: props.forLinkedTasks ? itemsForLinkedTasks() : filteredList,
    autoFocus: true,
    inputTestId: 'flow-run-schedule-picker',
    listTestId: 'flow-run-schedule-picker-list',
    searchPlaceholder: Messages.search(),
    onFilterChange: setSearchText,
    searchText: searchText,
  };

  return (
    <SingleSelectDropdown
      list={<FilteringList {...listProps} style={filteringListStyle} />}
      value={props.scheduleId}
      type={DropdownButtonType.Outline}
      onChange={onSelect}
      label={props.label}
      onOpen={onOpen}
      onClose={onClose}
      labelId={props.labelId}
    >
      <div style={{ whiteSpace: 'nowrap' }}>{getScheduleTextFromId(props.scheduleId)}</div>
    </SingleSelectDropdown>
  );
}
